import { create } from "zustand";
import type {
  MobileUiInboundAction,
  SessionLiveRuntimeEvent,
} from "../../../../packages/shared-types/src/index.js";

export type ConversationRuntimeState = {
  events: SessionLiveRuntimeEvent[];
  updatedAt: number;
};

type ConversationsUiState = {
  composerTextBySessionId: Record<string, string>;
  pendingActionBySessionId: Record<string, MobileUiInboundAction>;
  runtimeBySessionId: Record<string, ConversationRuntimeState>;
  waitingConversationId: string | null;
  downloadingId: string | null;
  setComposerText: (sessionId: string, text: string) => void;
  clearComposerText: (sessionId: string) => void;
  setPendingAction: (sessionId: string, action: MobileUiInboundAction) => void;
  clearPendingAction: (sessionId: string) => void;
  applyRuntimeEvent: (event: SessionLiveRuntimeEvent) => void;
  clearRuntimeEvents: (sessionId: string) => void;
  setWaitingConversationId: (sessionId: string | null) => void;
  setDownloadingId: (id: string | null) => void;
};

export const useConversationsUiStore = create<ConversationsUiState>((set) => ({
  composerTextBySessionId: {},
  pendingActionBySessionId: {},
  runtimeBySessionId: {},
  waitingConversationId: null,
  downloadingId: null,
  setComposerText: (sessionId, text) =>
    set((state) => ({
      composerTextBySessionId: {
        ...state.composerTextBySessionId,
        [sessionId]: text,
      },
    })),
  clearComposerText: (sessionId) =>
    set((state) => {
      const next = { ...state.composerTextBySessionId };
      delete next[sessionId];
      return { composerTextBySessionId: next };
    }),
  setPendingAction: (sessionId, action) =>
    set((state) => ({
      pendingActionBySessionId: {
        ...state.pendingActionBySessionId,
        [sessionId]: action,
      },
    })),
  clearPendingAction: (sessionId) =>
    set((state) => {
      const next = { ...state.pendingActionBySessionId };
      delete next[sessionId];
      return { pendingActionBySessionId: next };
    }),
  applyRuntimeEvent: (event) =>
    set((state) => {
      const current = state.runtimeBySessionId[event.sessionId];
      const nextEvents = [...(current?.events ?? []), event]
        .filter(
          (candidate, index, entries) =>
            entries.findIndex(
              (entry) =>
                entry.runId === candidate.runId &&
                entry.seq === candidate.seq &&
                entry.sessionId === candidate.sessionId,
            ) === index,
        )
        .sort((left, right) => {
          if (left.ts !== right.ts) {
            return left.ts - right.ts;
          }
          if (left.runId !== right.runId) {
            return left.runId.localeCompare(right.runId);
          }
          return left.seq - right.seq;
        })
        .slice(-64);
      return {
        runtimeBySessionId: {
          ...state.runtimeBySessionId,
          [event.sessionId]: {
            events: nextEvents,
            updatedAt: Date.now(),
          },
        },
      };
    }),
  clearRuntimeEvents: (sessionId) =>
    set((state) => {
      if (!state.runtimeBySessionId[sessionId]) {
        return state;
      }
      const next = { ...state.runtimeBySessionId };
      delete next[sessionId];
      return { runtimeBySessionId: next };
    }),
  setWaitingConversationId: (sessionId) => set({ waitingConversationId: sessionId }),
  setDownloadingId: (id) => set({ downloadingId: id }),
}));
